/**
 * useMicRefusal — le micro refusé en séance (`micRefusal`), branché :
 *  - les refus que la salle voit : `MediaDevicesError` de LiveKit sur le micro, que la demande vienne
 *    de la connexion (`<LiveKitRoom audio>`), d'un passage en Dialogue (`MuteCurrentUser`) ou d'un clic.
 *    Ceux de la connexion et des passages en Dialogue ne laissaient qu'une ligne dans la console ;
 *  - l'accès lu refusé à l'ouverture du live (`permissionDenied`, l'écran le lit) ;
 *  - ceux que l'élève provoque : son micro et « Activer mon micro » (`request`), « Réessayer » (`retry`),
 *    deux demandes à appeler dans le geste, que le guidage suit pendant que le navigateur pose sa
 *    question (`watchPermissionPrompt`).
 *
 * Une erreur Bugsnag par épisode (`mic_refused`, avec sa source) : la salle redemande à chaque passage
 * en Dialogue, une erreur par demande noierait la première. Chaque refus laisse son fil d'Ariane.
 */
import { type LocalParticipant } from "livekit-client"
import { useCallback, useEffect, useReducer, useRef } from "react"

import { watchPermissionPrompt } from "../../precall/permissionPrompt"
import { IS_WEB } from "../../shared/platform"
import { type EngineTelemetry } from "../../shared/telemetry"
import { useMediaDevicesError } from "./useMediaDevicesError"
import {
    initialMicRefusal,
    micBlockerOf,
    micRefusalReducer,
    micRefusalView,
    type MicBlocker,
    type MicRefusalSource,
    type MicRefusalView,
} from "./micRefusal"

/** Les réglages du micro de l'élève, ceux de son bouton. */
export const MIC_CAPTURE = { echoCancellation: true, noiseSuppression: true }

type MicParticipant = Pick<LocalParticipant, "on" | "off" | "setMicrophoneEnabled">

/**
 * La réponse du navigateur à une demande du micro, telle que la salle la voit : le micro acquis
 * (`audioStreamAcquired`) ou refusé (`mediaDevicesError` sur le micro). Pas la promesse de
 * `setMicrophoneEnabled`, que LiveKit ne tient qu'après la publication — et après une reconnexion en
 * cours : le guidage de la question resterait à l'écran une fois l'élève a répondu. La demande finie
 * sans l'un ni l'autre (le micro déjà là, une erreur d'avant la question) la clôt aussi.
 *
 * Rejetée au refus, pour le fil d'Ariane du guidage ; c'est la demande elle-même qui dit le refus
 * (`report`). Les événements se nomment par leur chaîne, comme `useMediaDevicesError`.
 */
function microphoneAnswer(participant: MicParticipant, request: Promise<unknown>): Promise<void> {
    return new Promise((resolve, reject) => {
        const answer = (refusal?: unknown) => {
            participant.off("audioStreamAcquired", onAcquired)
            participant.off("mediaDevicesError", onError)
            if (refusal === undefined) resolve()
            else reject(refusal)
        }
        const onAcquired = () => answer()
        const onError = (cause: Error, kind?: MediaDeviceKind) => {
            if (kind === "audioinput") answer(cause)
        }
        participant.on("audioStreamAcquired", onAcquired)
        participant.on("mediaDevicesError", onError)
        request.then(() => answer(), answer)
    })
}

export type UseMicRefusalOptions = {
    localParticipant: MicParticipant
    dialogue: boolean
    micOn: boolean
    /** L'accès au micro lu refusé (`useMediaPermissions`). */
    permissionDenied: boolean
    /** Une demande du geste a ouvert le micro (la porte du micro tenu, `useMicGate.opened`). */
    onOpened?: () => void
    telemetry: EngineTelemetry
}

export type MicRefusalController = MicRefusalView & {
    /** « Réessayer » : redemande le micro. À appeler dans le geste de l'élève. */
    retry: () => void
    /**
     * Ouvre le micro, demandé au navigateur dans le geste de l'élève (son micro, « Activer mon micro ») :
     * un refus passe au micro refusé, les autres échecs à Bugsnag.
     */
    request: () => void
    /** « Plus tard ». */
    dismiss: () => void
}

export function useMicRefusal(options: UseMicRefusalOptions): MicRefusalController {
    const { localParticipant, dialogue, micOn, permissionDenied, telemetry } = options
    const onOpenedRef = useRef(options.onOpened)
    onOpenedRef.current = options.onOpened
    const [state, send] = useReducer(micRefusalReducer, initialMicRefusal)
    // L'épisode en cours, lu par les rappels sans attendre le rendu : refusé, et son erreur déjà partie.
    // L'accès lu refusé ouvre l'épisode sans cause à envoyer ; la première demande refusée l'envoie.
    const refusedRef = useRef(false)
    const sentRef = useRef(false)

    const refuse = useCallback(
        (source: MicRefusalSource, blocker: MicBlocker, cause?: unknown) => {
            telemetry.transition(refusedRef.current ? "mic_refused" : "mic_ok", "mic_refused", source, { blocker })
            if (!sentRef.current && cause !== undefined) {
                sentRef.current = true
                telemetry.error("mic_refused", cause, { source, blocker })
            }
            refusedRef.current = true
            send({ type: "refused", source, blocker })
        },
        [telemetry]
    )

    const report = useCallback(
        (cause: unknown, source: MicRefusalSource) => {
            const blocker = micBlockerOf(cause)
            if (blocker != null) refuse(source, blocker, cause)
            return blocker != null
        },
        [refuse]
    )

    useMediaDevicesError(localParticipant, "audioinput", (cause) => report(cause, "room"))

    useEffect(() => {
        if (permissionDenied) refuse("permission", "browser")
    }, [permissionDenied, refuse])

    useEffect(() => {
        if (!micOn || !refusedRef.current) return
        refusedRef.current = false
        sentRef.current = false
        telemetry.transition("mic_refused", "mic_ok", "opened")
        send({ type: "opened" })
    }, [micOn, telemetry])

    // Le passage en Dialogue rouvre une boîte fermée ; au montage, rien n'est fermé.
    useEffect(() => {
        if (dialogue) send({ type: "dialogueEntered" })
    }, [dialogue])

    // Rien n'est attendu avant la demande : elle part dans le geste, la seule chance qu'un navigateur pose
    // (ou repose, après un refus) la question.
    const ask = useCallback(
        (source: "student" | "retry") => {
            const request = localParticipant.setMicrophoneEnabled(true, MIC_CAPTURE)
            // Avant le suivi de la demande : la porte s'ouvre dans le même rendu que la fin de la demande —
            // seulement si elle a publié un micro. LiveKit tient aussi à vide une seconde demande lancée
            // pendant la première (un double toucher), refusée : la porte resterait ouverte sur un refus.
            request.then(
                (publication) => {
                    if (publication != null) onOpenedRef.current?.()
                },
                () => undefined // le refus se dit plus bas
            )
            // La carte part à la réponse ; les boîtes du micro attendent la publication (`completion`).
            if (IS_WEB) void watchPermissionPrompt("microphone", microphoneAnswer(localParticipant, request), request)
            request.catch((cause: unknown) => {
                if (!report(cause, source)) telemetry.error(source === "retry" ? "retry_mic" : "toggle_mic", cause)
            })
        },
        [localParticipant, report, telemetry]
    )
    const retry = useCallback(() => {
        telemetry.transition("mic_refused", "mic_refused", "retry")
        ask("retry")
    }, [ask, telemetry])
    const request = useCallback(() => ask("student"), [ask])

    const dismiss = useCallback(() => send({ type: "dismissed" }), [])

    return { ...micRefusalView(state, { dialogue, micOn, web: IS_WEB }), retry, request, dismiss }
}
